import { useEffect, useState } from "react";
import { Alert, Box, FormControlLabel, MenuItem, Stack, Switch, TextField } from "@mui/material";
import { Visibility } from "@mui/icons-material";
import ButtonOne from "../../../../common/normal-button";
import { OTP_V2_GENERATE, OTP_V2_LIMITS } from "../../../../config/urls";
import {
  DEFAULT_CHAT_NUMBER,
  E164_PATTERN,
  OTP_CODE_LENGTH,
  OTP_CONTEXTS,
  OTP_MAX_ATTEMPTS,
  buildSendBody,
  findNodeConfig,
  setLastSandboxResult,
  useOtpApi,
} from "../../otpApi";
import OtpTemplatePreview from "./OtpTemplatePreview";
import OtpTestApi from "./OtpTestApi";
import {
  END_FAILED,
  HelperText,
  JsonBox,
  MappingRow,
  OutputsTable,
  ReadOnlyField,
  SectionTitle,
  SubSection,
} from "./OtpPanelParts";

const OUTPUTS = [
  { response: "200", detail: "OTP issued", route: "Verify OTP" },
  { response: "400 validation_error", detail: "Invalid phone, context or TTL", route: END_FAILED },
  { response: "402 insufficient_balance", detail: "Wallet can't cover the send", route: END_FAILED },
  { response: "422 template_invalid", detail: "Template missing, unapproved or wrong category", route: END_FAILED },
  { response: "429 rate_limited", detail: "Per-number hourly or daily limit", route: END_FAILED },
  { response: "502 dispatch_failed", detail: "WhatsApp send failed", route: END_FAILED },
];

const OtpSendPreset = ({ config, handleConfigChange, templates, nodes }) => {
  const callApi = useOtpApi();
  const [limits, setLimits] = useState(null);
  const [testPhone, setTestPhone] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    callApi(OTP_V2_LIMITS, { method: "GET" })
      .then(({ status, body }) => setLimits(status === 200 ? body : null))
      .catch(() => setLimits(null));
  }, [callApi]);

  const templateConfig = findNodeConfig(nodes, "otp_select_template");
  const template = (templates || []).find(
    (candidate) => candidate.template_name === templateConfig.template_name,
  );
  const ttl = config.ttl_seconds || 300;
  const ttlInvalid = ttl < 60 || ttl > 600;
  const testPhoneValid = E164_PATTERN.test(testPhone);

  const runTest = async () => {
    const result = await callApi(OTP_V2_GENERATE, {
      body: { ...buildSendBody(config, templateConfig, testPhone), sandbox: true },
    });
    if (result.status === 200) setLastSandboxResult(result.body);
    return result;
  };

  return (
    <Stack spacing={3}>
      <SectionTitle>API</SectionTitle>
      <SubSection>
        <ReadOnlyField label="Endpoint" value="POST {BASE_HOST_URL}/api/v2/otp/generate/" />
        <ReadOnlyField label="Connection" value="OTP API Connection" />
      </SubSection>

      <SectionTitle>Request Body</SectionTitle>
      <SubSection>
      <TextField
        required
        size="small"
        label="phone"
        value={config.phone ?? DEFAULT_CHAT_NUMBER}
        onChange={(event) => handleConfigChange("phone", event.target.value)}
        helperText="Use {{sender_phone}} for the current chat number, or a flow variable such as {{registered_mobile}}. It must resolve to a number with country code."
      />
      <TextField
        select
        required
        size="small"
        label="context"
        value={config.context || "login"}
        onChange={(event) => handleConfigChange("context", event.target.value)}
      >
        {OTP_CONTEXTS.map((context) => (
          <MenuItem key={context} value={context}>
            {context}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        required
        size="small"
        type="number"
        label="ttl_seconds"
        value={ttl}
        error={ttlInvalid}
        onChange={(event) => handleConfigChange("ttl_seconds", Number(event.target.value))}
        helperText={
          ttlInvalid
            ? "Must be between 60 and 600 seconds."
            : "60 to 600 seconds. Keep it equal to the template's code expiration."
        }
        inputProps={{ min: 60, max: 600, step: 30 }}
      />
      <MappingRow name="template_name">
        <ReadOnlyField label="From" value={templateConfig.template_name || "Not selected yet"} />
      </MappingRow>
      <MappingRow name="variables">
        <ReadOnlyField label="From" value="Template Variables" />
      </MappingRow>
      <JsonBox>{JSON.stringify(buildSendBody(config, templateConfig), null, 2)}</JsonBox>
      </SubSection>

      <SectionTitle>Response Mapping</SectionTitle>
      <SubSection>
        <MappingRow name="otp_ref_id" arrow="→">
          <TextField
            size="small"
            label="Store As"
            value={config.store_ref_as || "otp_ref_id"}
            onChange={(event) => handleConfigChange("store_ref_as", event.target.value)}
          />
        </MappingRow>
        <MappingRow name="expires_at" arrow="→">
          <TextField
            size="small"
            label="Store As"
            value={config.store_expires_as || "otp_expires_at"}
            onChange={(event) => handleConfigChange("store_expires_as", event.target.value)}
          />
        </MappingRow>
      </SubSection>

      <SectionTitle>Outputs</SectionTitle>
      <OutputsTable rows={OUTPUTS} />

      <SectionTitle>Fixed by the OTP Service</SectionTitle>
      <SubSection>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.5 }}>
          <ReadOnlyField label="Code Length" value={`${OTP_CODE_LENGTH} digits`} />
          <ReadOnlyField label="Code Type" value="Numeric" />
        </Box>
        <ReadOnlyField
          label="Max Attempts"
          value={OTP_MAX_ATTEMPTS}
          helperText="Set by the OTP service. Not configurable."
        />
      </SubSection>

      <SectionTitle>Limits (enforced by the OTP service)</SectionTitle>
      {limits ? (
        <SubSection>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.5 }}>
            <ReadOnlyField label="Resend Cooldown (s)" value={limits.resend_cooldown_seconds} />
            <ReadOnlyField label="Max Resends" value={limits.max_resends} />
            <ReadOnlyField label="Per Number / Hour" value={limits.hourly_cap} />
            <ReadOnlyField label="Per Number / Day" value={limits.daily_cap} />
          </Box>
          <HelperText>
            These come from your business's OTP settings and apply to every OTP flow. Each send
            is billed from the wallet as an Authentication conversation.
          </HelperText>
        </SubSection>
      ) : (
        <HelperText>Limits couldn't be loaded.</HelperText>
      )}

      <SectionTitle>Privacy</SectionTitle>
      <SubSection>
      <FormControlLabel
        control={
          <Switch
            checked={config.mask_replies ?? true}
            onChange={(event) => handleConfigChange("mask_replies", event.target.checked)}
          />
        }
        label="Mask OTP replies in inbox and chat logs"
      />
      <FormControlLabel
        control={
          <Switch
            checked={config.exclude_from_exports ?? true}
            onChange={(event) => handleConfigChange("exclude_from_exports", event.target.checked)}
          />
        }
        label="Exclude OTP values from exports and webhooks"
      />
      </SubSection>
      <Alert severity="info">
        While Verify OTP is waiting, a reply made only of digits is stored and shown as ••••••
        in the inbox, chat history, exports and logs. Masking applies if either option is on, and
        the code we send is always masked there.
      </Alert>

      <OtpTestApi
        onRun={runTest}
        disabled={!templateConfig.template_name || !testPhoneValid}
        disabledReason="Pick a template in Select OTP Template and enter a test number with country code."
      >
        <TextField
          size="small"
          label="Test phone number"
          placeholder="+919876543210"
          value={testPhone}
          error={Boolean(testPhone) && !testPhoneValid}
          onChange={(event) => setTestPhone(event.target.value.trim())}
          helperText="Sandbox test: nothing is sent on WhatsApp and nothing is billed. The response includes the code so you can try Verify OTP."
        />
      </OtpTestApi>

      <ButtonOne
        variant="outlined"
        title="Preview OTP Message"
        startIcon={<Visibility />}
        onClick={() => setPreviewOpen(true)}
        disabled={!template}
        sx={{ alignSelf: "flex-start" }}
      />
      <OtpTemplatePreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        template={template}
        variables={templateConfig.variables}
      />
    </Stack>
  );
};

export default OtpSendPreset;
